import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Drawer, Popconfirm, Row, Space, Table, Typography, Card, Tooltip, Tag, Input, Select, DatePicker, Checkbox } from 'antd';
import { SearchOutlined, FileExcelOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { openNotification } from '../../../utils';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { apiFetch } from 'services/apiClient';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { listExternalDocuments, deleteExternalDocument, listExternalDocumentsAll } from '../services/external-documentService';
import ExternalDocumentForm from './Form';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

type SortDirection = 'ascend' | 'descend' | null;

export default function ExternalDocumentList({ referenceId }: { referenceId?: number }) {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [exporting, setExporting] = useState(false);
  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  // committed
  // free-text search
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pathContains, setPathContains] = useState('');
  const [pubFrom, setPubFrom] = useState<string | undefined>(undefined);
  const [pubTo, setPubTo] = useState<string | undefined>(undefined);
  const [lastRevFrom, setLastRevFrom] = useState<string | undefined>(undefined);
  const [lastRevTo, setLastRevTo] = useState<string | undefined>(undefined);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);
  const [docTypeEq, setDocTypeEq] = useState<string | undefined>(undefined);
  const [attrOptions, setAttrOptions] = useState<any[]>([]);
  const [attrIds, setAttrIds] = useState<number[]>([]);
  // Only show items where document is missing (null/empty)
  const [docMissingOnly, setDocMissingOnly] = useState<boolean>(false);
  // pending
  const [pPathContains, setPPathContains] = useState('');
  const [pPubFrom, setPPubFrom] = useState<any>(null);
  const [pPubTo, setPPubTo] = useState<any>(null);
  const [pLastRevFrom, setPLastRevFrom] = useState<any>(null);
  const [pLastRevTo, setPLastRevTo] = useState<any>(null);
  const [pIsActive, setPIsActive] = useState<'all' | 'true' | 'false'>('all');
  const [pDocTypeEq, setPDocTypeEq] = useState<string | undefined>(undefined);
  const [pAttrIds, setPAttrIds] = useState<number[]>([]);
  const [pDocMissingOnly, setPDocMissingOnly] = useState<boolean>(false);

  const documentTypeMap: Record<string, string> = { "LEGISLATION": "Mevzuat", "STANDARD": "Standart", "GUIDE": "Rehber", "NORM": "Norm" };
  const DEFAULT_SORT_FIELD = 'documentType';
  const DEFAULT_SORT_DIRECTION: SortDirection = 'ascend';
  const [sortField, setSortField] = useState<string | null>(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);
  const sortDirectionsCycle: SortDirection[] = ['ascend', 'descend', null];
  const apiSortParam = useMemo(() => {
    if (sortField && sortDirection) {
      return `${sortField},${sortDirection === 'ascend' ? 'asc' : 'desc'}`;
    }
    return undefined;
  }, [sortField, sortDirection]);
  const getSortOrderFor = (field: string) => (sortField === field ? (sortDirection || undefined) : undefined);
  const debouncedApplySearch = useMemo(() => debounce((val: string) => { setSearch(val); }, 300), []);
  useEffect(() => () => { debouncedApplySearch.cancel(); }, [debouncedApplySearch]);
  const location = useLocation();
  const isExternalDocumentsPage = location.pathname === '/settings/external-documents' || location.pathname === '/external-documents';
  const wasExternalDocumentsPageRef = useRef<boolean>(isExternalDocumentsPage);
  const resetState = useCallback(() => {
    debouncedApplySearch.cancel();
    setData([]);
    setTotal(0);
    setPage(1);
    setPageSize(10);
    setSortField(DEFAULT_SORT_FIELD);
    setSortDirection(DEFAULT_SORT_DIRECTION);
    setSearchInput('');
    setSearch('');
    setPathContains('');
    setPubFrom(undefined);
    setPubTo(undefined);
    setLastRevFrom(undefined);
    setLastRevTo(undefined);
    setIsActiveFilter(undefined);
    setDocTypeEq(undefined);
    setAttrIds([]);
    setDocMissingOnly(false);
    setPPathContains('');
    setPPubFrom(null);
    setPPubTo(null);
    setPLastRevFrom(null);
    setPLastRevTo(null);
    setPIsActive('all');
    setPDocTypeEq(undefined);
    setPAttrIds([]);
    setPDocMissingOnly(false);
    setOpen(false);
    setFilterOpen(false);
    setEditingId(undefined);
  }, [DEFAULT_SORT_DIRECTION, DEFAULT_SORT_FIELD, debouncedApplySearch]);
  useEffect(() => {
    if (isExternalDocumentsPage && !wasExternalDocumentsPageRef.current) {
      resetState();
    }
    wasExternalDocumentsPageRef.current = isExternalDocumentsPage;
  }, [isExternalDocumentsPage, resetState]);
  const buildFilterQuery = useCallback(() => {
    const filters: string[] = [];
    if (search && search.length >= 2) filters.push('search.contains=' + encodeURIComponent(search));
    if (pathContains) filters.push('documentFilePath.contains=' + encodeURIComponent(pathContains));
    if (docMissingOnly) filters.push('documentFilePath.specified=false');
    if (pubFrom) filters.push('publicationDate.greaterThanOrEqual=' + pubFrom);
    if (pubTo) filters.push('publicationDate.lessThanOrEqual=' + pubTo);
    if (lastRevFrom) filters.push('lastRevisionDate.greaterThanOrEqual=' + lastRevFrom);
    if (lastRevTo) filters.push('lastRevisionDate.lessThanOrEqual=' + lastRevTo);
    if (typeof isActiveFilter === 'boolean') filters.push('isActive.equals=' + isActiveFilter);
    if (docTypeEq) filters.push('documentType.equals=' + docTypeEq);
    if (attrIds.length) attrIds.forEach(id => filters.push('attributesId.in=' + id));
    return filters.join('&');
  }, [attrIds, docMissingOnly, docTypeEq, isActiveFilter, lastRevFrom, lastRevTo, pathContains, pubFrom, pubTo, search]);
  const fetchData = useCallback(async () => {
    const query = buildFilterQuery();
    const res = await listExternalDocuments(page - 1, pageSize, apiSortParam, query);
    setData(res.content);
    setTotal(res.totalElements);
  }, [apiSortParam, buildFilterQuery, page, pageSize]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { apiFetch('/attributes?isActive=true').then(setAttrOptions); }, []);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id: number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const openFilter = () => {
    setPPathContains(pathContains);
    setPPubFrom(pubFrom ? dayjs(pubFrom) : null);
    setPPubTo(pubTo ? dayjs(pubTo) : null);
    setPLastRevFrom(lastRevFrom ? dayjs(lastRevFrom) : null);
    setPLastRevTo(lastRevTo ? dayjs(lastRevTo) : null);
    setPIsActive(isActiveFilter === undefined ? 'all' : isActiveFilter ? 'true' : 'false');
    setPDocTypeEq(docTypeEq);
    setPAttrIds(attrIds);
    setPDocMissingOnly(docMissingOnly);
    setFilterOpen(true);
  };
  const closeFilter = () => setFilterOpen(false);
  const clearFilter = () => {
    setPPathContains('');
    setPPubFrom(null); setPPubTo(null); setPLastRevFrom(null); setPLastRevTo(null);
    setPIsActive('all'); setPDocTypeEq(undefined); setPAttrIds([]); setPDocMissingOnly(false);
    setPathContains('');
    setPubFrom(undefined); setPubTo(undefined); setLastRevFrom(undefined); setLastRevTo(undefined);
    setIsActiveFilter(undefined);
    setDocTypeEq(undefined);
    setAttrIds([]);
    setDocMissingOnly(false);
    setPage(1);
  };
  const handleDelete = async (id: number) => {
    try {
      await deleteExternalDocument(id);
      openNotification('success', 'Başarılı', 'Dış Kaynaklı Doküman Silindi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Dış Kaynaklı Doküman silme işlemi başarısız');
    }
  };
  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    const nextPageSize = pagination?.pageSize || pageSize;
    let nextPage = pagination?.current || 1;
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      nextPage = 1;
    }

    if (!Array.isArray(sorter)) {
      const rawField = sorter?.field ? String(sorter.field) : null;
      const nextDirection: SortDirection = sorter?.order ?? null;
      const nextField = nextDirection && rawField ? rawField : null;
      if (nextField !== sortField || nextDirection !== sortDirection) {
        setSortField(nextField);
        setSortDirection(nextField ? nextDirection : null);
        nextPage = 1;
      }
    }

    if (nextPage !== page) {
      setPage(nextPage);
    }
  };
  const handleExportExcel = useCallback(async () => {
    try {
      setExporting(true);
      const query = buildFilterQuery();
      const list = await listExternalDocumentsAll(apiSortParam, query);
      const headers = [
        'Doküman Türü',
        'Doküman No',
        'Doküman Adı',
        'Yayın / Kabul / Revizyon Tarihi',
        'Son Gözden Geçirme Tarihi',
      ];
      const rows = Array.isArray(list) ? list.map((doc: any) => [
        documentTypeMap[doc?.documentType] ?? doc?.documentType ?? '',
        doc?.documentNumber ?? '',
        doc?.documentName ?? '',
        doc?.publicationDate ? dayjs(doc.publicationDate).format(APP_LOCAL_DATE_FORMAT) : '',
        doc?.lastRevisionDate ? dayjs(doc.lastRevisionDate).format(APP_LOCAL_DATE_FORMAT) : '',
      ]) : [];
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Dis Kaynakli Dokumanlar');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, 'dis-kaynakli-dokumanlar.xlsx');
    } catch (error) {
      console.error('External documents excel export error:', error);
      openNotification('error', 'Hata', 'Excel dışa aktarılırken bir hata oluştu');
    } finally {
      setExporting(false);
    }
  }, [apiSortParam, buildFilterQuery, documentTypeMap]);
  const columns = [
    {
      title: 'Doküman Türü',
      dataIndex: 'documentType',
      sorter: true,
      sortDirections: sortDirectionsCycle,
      sortOrder: getSortOrderFor('documentType'),
      render: (v: any) => {
        const label = documentTypeMap[v] ?? v;
        return <Tag color='processing'>{label}</Tag>;
      }
    },
    {
      title: 'Doküman No',
      dataIndex: 'documentNumber',
      sorter: true,
      sortDirections: sortDirectionsCycle,
      sortOrder: getSortOrderFor('documentNumber'),
    },
    {
      title: 'Doküman Adı',
      dataIndex: 'documentName',
      sorter: true,
      sortDirections: sortDirectionsCycle,
      sortOrder: getSortOrderFor('documentName'),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      sorter: true,
      sortDirections: sortDirectionsCycle,
      sortOrder: getSortOrderFor('isActive'),
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'red'}>{v ? 'Aktif' : 'Pasif'}</Tag>
      )
    },
    {
      title: 'Parametreler', dataIndex: 'attributes', sorter: false, render: (_: any, r: any) => {
        const attrs: any[] = Array.isArray(r?.attributes) ? r.attributes : [];
        const names = attrs
          .map((a: any) => (typeof a === 'object' ? a?.name : a))
          .filter((n: any) => !!n)
          .map((n: any) => String(n));
        if (!names.length) return '';
        const sorted = names.slice().sort((a: string, b: string) => a.localeCompare(b, 'tr'));
        return (
          <Space size={4} wrap>
            {sorted.map((n: string) => (
              <Tag key={n}>{n}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Yayın Tarihi',
      dataIndex: 'publicationDate',
      sorter: true,
      sortDirections: sortDirectionsCycle,
      sortOrder: getSortOrderFor('publicationDate'),
      render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : ''
    },
    {
      title: 'Son Gözden Geçirme',
      dataIndex: 'lastRevisionDate',
      sorter: true,
      sortDirections: sortDirectionsCycle,
      sortOrder: getSortOrderFor('lastRevisionDate'),
      render: (v: string) => v ? dayjs(v).format(APP_LOCAL_DATE_FORMAT) : ''
    },
    {
      title: 'Doküman',
      dataIndex: 'documentFilePath',
      sorter: true,
      sortDirections: sortDirectionsCycle,
      sortOrder: getSortOrderFor('documentFilePath'),
      render: (v: string) => v ? <a href={v} target='_blank' rel='noopener noreferrer'>Görüntüle</a> : ''
    },
    {
      title: 'İşlemler', render: (_: any, r: any) => (
        <Space>
          <Tooltip title='Düzenle'>
            <Button
              icon={<img src={PenIcon} alt="pen" className="icon" />}
              shape="circle"
              onClick={() => openEdit(r.id)}
              className="icon-button"
            />
          </Tooltip>
          <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
            <Tooltip title='Sil'>
              <Button
                icon={<img src={TrashIcon} alt="trash" className="icon" />}
                shape="circle"
                className="icon-button ml-12px"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];
  return (
    <>

      <Card
        className='br-30px kontrolx-card'
        title={(
          <Row align='bottom'>
            <Typography.Title level={4} style={{ margin: 0 }}>Dış Kaynaklı Doküman Listesi</Typography.Title>
            <Typography.Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
              <strong>({total})</strong> kayıt listelendi
            </Typography.Text>
          </Row>
        )}
        extra={
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-form d-input-filter'
              style={{ width: 240 }}
              value={searchInput}
              onChange={(e) => {
                const val = e.target.value || '';
                setSearchInput(val);
                if (val.length >= 2) {
                  debouncedApplySearch(val);
                } else if (val.length === 0) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Tooltip title="Dışa Aktar">
              <Button
                shape='circle'
                className='excel-icon-button'
                onClick={handleExportExcel}
                aria-label='Excel indir'
              >
                <FileExcelOutlined />
              </Button>
            </Tooltip>
            <Button type='primary' onClick={openFilter}>Filtrele</Button>
            <Button type='primary' onClick={openCreate}>Ekle</Button>
          </Space>
        }
      >
        <Table
          rowKey='id'
          dataSource={data}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
        />
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? 'Dış Kaynaklı Doküman Düzenle' : 'Dış Kaynaklı Doküman Ekle'}>
        <ExternalDocumentForm id={editingId} onSuccess={() => { handleClose(); fetchData(); }} />
      </Drawer>
      <Drawer open={filterOpen} onClose={closeFilter} destroyOnClose width={480} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>

          {/* <div>
            <Typography.Text>Dosya Yolu (içerir)</Typography.Text>
            <Input allowClear style={{ marginTop: 6 }} value={pPathContains} onChange={(e)=>setPPathContains(e.target.value)} />
          </div> */}
          <div>
            <Typography.Text>Doküman Türü</Typography.Text>
            <Select allowClear style={{ width: '100%', marginTop: 6 }} value={pDocTypeEq} onChange={(v) => setPDocTypeEq(v)}>
              {Object.keys(documentTypeMap).map(k => (
                <Select.Option key={k} value={k}>{documentTypeMap[k]}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Typography.Text>Parametreler</Typography.Text>
            <Select
              mode='multiple'
              allowClear
              showSearch
              filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
              placeholder='Seçiniz'
              style={{ width: '100%', marginTop: 6 }}
              value={pAttrIds}
              onChange={(vals: number[]) => setPAttrIds(vals)}
            >
              {attrOptions.filter((o: any) => o?.isActive === true).map((o: any) => (
                <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Typography.Text>Yayın / Kabul / Revizyon Tarihi</Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={[pPubFrom, pPubTo] as any}
              onChange={(vals) => { setPPubFrom(vals?.[0] || null); setPPubTo(vals?.[1] || null); }}
              format={APP_LOCAL_DATE_FORMAT}
            />
          </div>
          <div>
            <Typography.Text>Son Gözden Geçirme Tarihi</Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={[pLastRevFrom, pLastRevTo] as any}
              onChange={(vals) => { setPLastRevFrom(vals?.[0] || null); setPLastRevTo(vals?.[1] || null); }}
              format={APP_LOCAL_DATE_FORMAT}
            />
          </div>

          <div>
            <Checkbox checked={pDocMissingOnly} onChange={(e) => setPDocMissingOnly(e.target.checked)}>
              Doküman Yüklenmemiş Olanlar
            </Checkbox>
          </div>


          <div>
            <Typography.Text>Durum</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={pIsActive}
              onChange={(val: 'all' | 'true' | 'false') => setPIsActive(val)}
              options={[{ value: 'all', label: 'Tümü' }, { value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }]}
            />
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={clearFilter}>Temizle</Button>
            <Button type='primary' onClick={() => {
              setPathContains(pPathContains);
              setPubFrom(pPubFrom ? pPubFrom.format('YYYY-MM-DD') : undefined);
              setPubTo(pPubTo ? pPubTo.format('YYYY-MM-DD') : undefined);
              setLastRevFrom(pLastRevFrom ? pLastRevFrom.format('YYYY-MM-DD') : undefined);
              setLastRevTo(pLastRevTo ? pLastRevTo.format('YYYY-MM-DD') : undefined);
              setIsActiveFilter(pIsActive === 'all' ? undefined : pIsActive === 'true');
              setDocTypeEq(pDocTypeEq);
              setAttrIds(pAttrIds);
              setDocMissingOnly(pDocMissingOnly);
              closeFilter();
            }}>Filtrele</Button>
          </Space>
        </Space>
      </Drawer>
    </>
  );
}
